import { useEffect, useContext, useMemo } from 'react'
import { withRouter } from 'react-router-dom'
import { ContentHeader } from 'components/shared/content'
import * as H from 'history'
import { GlobalStateContext } from 'contexts/global_state_context'
import { useEffectSkipFirst, useForm } from 'utils/hooks'
import { SelectField } from 'components/shared/input'
import moment from 'moment'
import { A4 } from 'components/shared/print'
import { Button } from 'react-bootstrap'
import { Flex } from 'components/shared/flex'
import { selectYears, setPageSize, zeroPadding } from 'utils/util'
import { TransportationReport } from 'entities/transportation_report'
import { Sample, SampleSearchForm } from 'entities/sample'
import { useFetchInstitutionsApi } from 'api/institution'
import { Institution, InstitutionSearchForm } from 'entities/institution'
import { useFetchSamplesApi } from 'api/sample'
import { LedgerFooter, LedgerHeader } from 'components/ledger/ledger_componet'

type Props = {
    history: H.History
}

const CheckInLedgerPage = (props: Props) => {
    const form = useForm<{ year: string; month: string }>({ year: moment().format('YYYY'), month: moment().format('MM') })
    const searchForm = useForm<SampleSearchForm>({ ordering: 'sample_inspection_date', perPage: 10000000, withTransportationReports: true })
    const listApi = useFetchSamplesApi(searchForm)
    const institutionSearchForm = useForm<InstitutionSearchForm>({ perPage: 10000000 })
    const institutionApi = useFetchInstitutionsApi(institutionSearchForm)
    const globalState = useContext(GlobalStateContext)
    const checkInLedgerPaperRows = 8

    useEffect(() => {
        setPageSize('landscape')
        institutionApi.execute()
    }, [])

    const institutions = useMemo(() => {
        globalState.setLoading(institutionApi.loading)
        if (institutionApi.isSuccess()) {
            return institutionApi.response.results
        } else return []
    }, [institutionApi.loading])

    useEffectSkipFirst(() => {
        globalState.setLoading(listApi.loading || institutionApi.loading)
    }, [listApi.loading, institutionApi.loading])

    useEffect(() => {
        searchForm.update((f) => {
            f.inspectionDateAfter = `${form.object.year}-${form.object.month}-01`
            f.inspectionDateBefore = `${form.object.year}-${form.object.month}-${new Date(Number(form.object.year), Number(form.object.month), 0).getDate()}`
        })
    }, [form.object])

    return (
        <>
            <ContentHeader
                className="no-print"
                headerTitle="検体受付作業日誌"
                right={
                    <Flex>
                        <SelectField form={form} attr="year" selectItems={selectYears()} style={{ width: 120 }} />
                        <SelectField
                            form={form}
                            attr="month"
                            selectItems={Array(12)
                                .fill(0)
                                .map((_, i) => ({ label: `${i + 1}月`, value: zeroPadding(i + 1, 2) }))}
                        />
                    </Flex>
                }
                left={
                    <>
                        <Button
                            onClick={() => {
                                window.print()
                            }}
                        >
                            印刷プレビュー
                        </Button>
                    </>
                }
            />
            {institutionApi.isSuccess() && (
                <>
                    <CheckInLedgerPaper samples={listApi.response.results} institutions={institutions} checkInLedgerPaperRows={checkInLedgerPaperRows} />
                </>
            )}
        </>
    )
}

export default withRouter(CheckInLedgerPage)

type CheckInLedgerPaperProps = {
    samples: Sample[]
    institutions: Institution[]
    checkInLedgerPaperRows: number
}

const CheckInLedgerPaper = (props: CheckInLedgerPaperProps) => {
    const nRow = props.checkInLedgerPaperRows
    const page = Math.ceil(props.samples.length / nRow)

    return (
        <>
            {Array(page)
                .fill(0)
                .map((_, pageIndex) => {
                    return (
                        <A4 key={'key' + pageIndex} padding={0} landscape={true}>
                            <div className={'preview-result'} style={{ width: '100%', height: '100%', padding: 20, marginTop: 15 }}>
                                <LedgerHeader
                                    headerTitle="検体受付作業日誌"
                                    recordTypeNum="RCL00002"
                                    originalDocument="LSOP-RCL003"
                                    versionNum="5"
                                    timestamp={moment().format('YYYYMMDDHHmm')}
                                ></LedgerHeader>

                                <div style={{ fontSize: 10, lineHeight: '11px' }}>
                                    <table style={{ width: '100%', border: 'solid 2px' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ border: 'solid 1px', textAlign: 'center', width: 80, padding: '0 5px' }}>検体コード</th>
                                                <th style={{ border: 'solid 1px', width: 180, padding: '0 5px', textAlign: 'center' }}>医療機関等名</th>
                                                <th style={{ border: 'solid 1px', textAlign: 'center' }}>受付年月日</th>
                                                <th style={{ border: 'solid 1px', textAlign: 'center' }}>開始時刻</th>
                                                <th style={{ border: 'solid 1px', textAlign: 'center' }}>依頼書等枚数 ※１</th>
                                                <th style={{ border: 'solid 1px', textAlign: 'center' }}>総受領検体数</th>
                                                <th style={{ border: 'solid 1px', textAlign: 'center' }}>受付時温度</th>
                                                <th style={{ border: 'solid 1px', textAlign: 'center' }}>照合結果 ※２</th>
                                                <th style={{ border: 'solid 1px', textAlign: 'center' }}>受付担当者</th>
                                                <th style={{ border: 'solid 1px', textAlign: 'center' }}>終了時刻</th>
                                                <th style={{ border: 'solid 1px', textAlign: 'center', width: 180 }}>備考※３</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Array(nRow)
                                                .fill(0)
                                                .map((_, index) => {
                                                    const s: Sample = props.samples[index + pageIndex * nRow]
                                                    if (s !== undefined) {
                                                        const t: TransportationReport = s.transportationReport!
                                                        const endTime = new Date(s.createdAt!)
                                                        endTime.setMinutes(new Date(s.createdAt!).getMinutes() + 10)
                                                        return (
                                                            <tr key={index}>
                                                                <td style={{ border: 'solid 1px', padding: '0 5px', textAlign: 'center' }}>{s.code}</td>
                                                                <td style={{ border: 'solid 1px', padding: '0 5px' }}>
                                                                    {props.institutions.find((institution) => institution.id === s?.institutionId)?.name}
                                                                </td>
                                                                <td style={{ border: 'solid 1px', textAlign: 'center', padding: '15px 0' }}>
                                                                    {moment(s?.inspectionDate).format('YYYY/MM/DD')}
                                                                </td>
                                                                <td style={{ border: 'solid 1px', textAlign: 'center' }}>
                                                                    {moment(s?.inspectionDate).format('HH:mm')}
                                                                </td>
                                                                <td style={{ border: 'solid 1px', textAlign: 'center' }}>{`1 枚`}</td>
                                                                <td style={{ border: 'solid 1px', textAlign: 'center' }}>{`1 検体`}</td>
                                                                <td style={{ border: 'solid 1px', textAlign: 'center' }}>{t.temperature}℃</td>
                                                                <td style={{ border: 'solid 1px', textAlign: 'center' }}>
                                                                    {<input type={'checkbox'} checked />}
                                                                </td>
                                                                <td style={{ border: 'solid 1px', textAlign: 'center' }}>{s.receiptUser?.firstName}</td>
                                                                <td style={{ border: 'solid 1px', textAlign: 'center' }}>
                                                                    {moment(s?.inspectionDate).add('minute', 10).format('HH:mm')}
                                                                </td>
                                                                <td style={{ border: 'solid 1px', padding: '0 5px' }}>
                                                                    {s.note
                                                                        ? s.note?.split('\n').map((e, index) => {
                                                                              return <div key={index}>{e}</div>
                                                                          })
                                                                        : '特になし'}
                                                                </td>
                                                            </tr>
                                                        )
                                                    }
                                                })}
                                        </tbody>
                                    </table>
                                </div>
                                <div>
                                    <div style={{ fontSize: 11, margin: '5px 0px' }}>
                                        <div>*1 問診票兼検査依頼書の合計枚数</div>
                                        <div>*2 問診票兼検査依頼書と受付検体が同一患者のものであればチェックをする。</div>
                                        <div>
                                            *3
                                            事故処理記録、申し送り事項、医療機関等への問合せ内容等を記載する。また、測定に進まず廃棄となる検体についても記載する。
                                        </div>
                                    </div>
                                </div>

                                <LedgerFooter page={pageIndex + 1}></LedgerFooter>
                            </div>
                        </A4>
                    )
                })}
        </>
    )
}
